import React, { useEffect, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { FormikRadioGroup } from './FormikRadioGroup';
import { Box } from '@material-ui/core';
import type { FormField } from '../interfaces';
import { findCamundaValidation, getLocalizedLabel } from '../utils';
import { FormikNumericFormat } from './FormikNumericFormat';
import classNames from 'classnames';
import { getFormFieldStyles } from '../formFieldStyles';

export const BudsjettertSelector = ({
    isRequired,
    isReadOnly,
    field,
    fields,
    locale,
}: {
    isRequired?: boolean;
    isReadOnly?: boolean;
    field?: FormField;
    fields?: Array<FormField>;
    locale: string;
}) => {
    const classes = getFormFieldStyles();
    const { setFieldValue } = useFormikContext();
    const [budsjettertFormikField] = useField(field?.id as string);
    const [aarsbudsjettFormikField, aarsbudsjettFormikMeta] =
        useField('aarsbudsjett');

    const aarsbudsjettField = fields?.find(
        (f) => f.id === aarsbudsjettFormikField?.name,
    );
    const [showConditionalField, setShowConditionalField] = useState(undefined);

    const aarsbudsjettFieldIsRequired = findCamundaValidation(
        'required',
        aarsbudsjettField?.validation,
    );

    const aarsbudsjettFieldIsReadOnly = findCamundaValidation(
        'readonly',
        aarsbudsjettField?.validation,
    );

    useEffect(() => {
        if (
            budsjettertFormikField?.value === 'false' ||
            showConditionalField === false
        ) {
            if (!aarsbudsjettField?.value && aarsbudsjettFieldIsRequired) {
                setFieldValue(aarsbudsjettField?.id as string, 0);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [budsjettertFormikField?.value, showConditionalField]);

    return (
        <Box className={classes.container}>
            <FormikRadioGroup
                {...budsjettertFormikField}
                id={field?.id}
                label={getLocalizedLabel(
                    `project.formFields.${field?.id}`,
                    field?.label,
                    field?.id,
                )}
                isRequired={isRequired}
                isReadOnly={isReadOnly}
                callback={setShowConditionalField}
                locale={locale}
            />
            {showConditionalField ||
            budsjettertFormikField?.value === 'true' ||
            (aarsbudsjettFormikMeta.touched &&
                Boolean(aarsbudsjettFormikMeta.error)) ? (
                <Box paddingTop="15px">
                    <FormikNumericFormat
                        thousandSeparator={' '}
                        decimalSeparator=","
                        variant="outlined"
                        className={classNames({
                            [classes.editableField]: !isReadOnly,
                            [classes.readOnlyField]: isReadOnly,
                        })}
                        id={aarsbudsjettField?.id}
                        isRequired={
                            aarsbudsjettFieldIsRequired &&
                            (showConditionalField ||
                                budsjettertFormikField?.value === 'true')
                        }
                        isReadOnly={isReadOnly || aarsbudsjettFieldIsReadOnly}
                        label={getLocalizedLabel(
                            `project.formFields.aarsbudsjett`,
                            aarsbudsjettField?.label,
                            aarsbudsjettField?.id,
                        )}
                        defaultValue={
                            aarsbudsjettField?.value ||
                            aarsbudsjettField?.defaultValue
                        }
                        helperText={
                            aarsbudsjettFormikMeta.touched
                                ? aarsbudsjettFormikMeta.error
                                : ''
                        }
                        error={
                            aarsbudsjettFormikMeta.touched &&
                            Boolean(aarsbudsjettFormikMeta.error)
                        }
                    />
                </Box>
            ) : null}
        </Box>
    );
};
